/**
 * SSE streaming client for POST-based Server-Sent Event streams.
 *
 * WHY THIS FILE EXISTS:
 * Axios (used everywhere else via apiClient) cannot consume a ReadableStream
 * response body — it buffers the entire response before resolving. SSE requires
 * reading the body chunk-by-chunk as it arrives. This module uses the native
 * fetch API instead and re-implements the auth concerns that Axios interceptors
 * normally handle automatically (token injection, 401 retry, camelCase
 * conversion). Token refresh itself is not reimplemented — it delegates to the
 * shared lock in authRefresh.ts so a stream's refresh can never race one
 * triggered elsewhere in the app.
 */

import { useAuthStore } from "../stores/useAuthStore";
import { camelCaseObject } from "./utils";
import { refreshAccessToken } from "./authRefresh";
import { API_BASE_URL } from "./env";

export type SSEErrorReason = "network" | "auth" | "stream-closed";

export interface SSEClientOptions<TData> {
  /** Path relative to VITE_API_BASE_URL, e.g. "/api/v1/facilities/1/.../render/stream" */
  url: string;
  /** Request body — serialised as JSON. Built by CustomDashboardService.buildStreamPayload. */
  body: unknown;
  /**
   * Called for every parsed SSE record, including the final "done" event.
   * Callers receive the done payload here so they can extract rendered/failed counts
   * before the stream closes — onDone() fires immediately after as a close signal.
   */
  onEvent: (eventType: string, data: TData) => void;
  /** Called when the stream cannot be opened or closes unexpectedly before "done". */
  onError: (reason: SSEErrorReason) => void;
  /** Called after the "done" event has been dispatched and the reader loop exits cleanly. */
  onDone: () => void;
}

/**
 * Returns the current access token if one is stored, otherwise attempts a
 * pre-flight refresh.
 *
 * WHY NOT CHECK EXPIRY PROACTIVELY:
 * The auth store does not persist token expiry time, so we cannot tell whether a
 * non-null token is expired without decoding the JWT. Rather than add that
 * dependency, we optimistically use the stored token and fall back to a 401 retry
 * in openSSEStream() for the rare case where the token expires between this check
 * and the fetch. Dashboard renders are short enough (<60 s) that mid-stream expiry
 * is not a concern for now.
 */
export async function getValidToken(): Promise<string | null> {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) return accessToken;
  return refreshAccessToken();
}

/**
 * Parses complete SSE records out of a running text buffer and calls onEvent for
 * each one. Returns whatever is left in the buffer after the last complete record
 * (i.e. an incomplete record that straddles a chunk boundary).
 *
 * SSE RECORD FORMAT (RFC 8895):
 *   event: <type>\n
 *   data: <json>\n
 *   \n                ← blank line marks the end of a record
 *
 * WHY BUFFER ACROSS CHUNKS:
 * The ReadableStream reader delivers chunks in arbitrary sizes. A single SSE record
 * (event + data lines + blank line) can be split across multiple chunks. We must
 * accumulate into a string buffer and only parse once we see the "\n\n" delimiter.
 *
 * WHY doneFired IS A MUTABLE REF OBJECT ({ value: boolean }) AND NOT A LOCAL BOOL:
 * parseSseBuffer is called on each chunk iteration inside the while loop. A plain
 * boolean local to openSSEStream would require returning it or closing over it —
 * using a mutable object passed by reference is simpler and avoids extra state in
 * the outer scope.
 *
 * WHY "done" GOES THROUGH onEvent TOO:
 * The StreamDoneEvent payload carries rendered/failed counts that callers need
 * (e.g. to set failedCount in the reducer). Routing it through onEvent keeps the
 * callback surface simple — callers only need one handler — then onDone() fires
 * as a pure "stream is finished" signal with no payload.
 */
export function parseSseBuffer<TData>(
  buffer: string,
  onEvent: (eventType: string, data: TData) => void,
  doneFired: { value: boolean },
  onDone: () => void,
): string {
  // sse-starlette (and some other SSE servers) emit CRLF (\r\n) line endings.
  // The SSE spec allows CRLF, CR, or LF — normalise to LF so the rest of the
  // parser only needs to handle one case.
  let remaining = buffer.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  let idx: number;

  while ((idx = remaining.indexOf("\n\n")) !== -1) {
    const record = remaining.slice(0, idx);
    remaining = remaining.slice(idx + 2);

    const eventType = record.match(/^event: (.*)$/m)?.[1]?.trim() ?? "message";
    // The space after "data:" is optional per the SSE spec — this backend
    // omits it for a bare, payload-less marker (e.g. `data:` with nothing
    // after, for event: stale). A field-presence match distinguishes that
    // from "no data: line at all" (keepalive comments, the retry: hint),
    // which must still be skipped entirely rather than dispatched as an
    // event with no data.
    const dataFieldMatch = record.match(/^data: ?(.*)$/m);
    if (!dataFieldMatch) continue;
    // trim() strips any residual \r that wasn't part of a \r\n pair
    const dataLine = dataFieldMatch[1].trim();

    let parsed: unknown;
    if (dataLine === "") {
      // Bare marker event (e.g. event: stale) — no payload to parse.
      parsed = undefined;
    } else {
      try {
        parsed = JSON.parse(dataLine);
      } catch {
        // Malformed JSON from BE — skip silently rather than crashing the stream
        continue;
      }
    }

    // Apply the same camelCase conversion that Axios/apiClient normally handles
    const data = camelCaseObject(parsed) as TData;
    onEvent(eventType, data);

    if (eventType === "done") {
      doneFired.value = true;
      onDone();
    }
  }

  return remaining;
}

/**
 * Shared helper that issues the streaming fetch request with the given token.
 * Extracted so it can be called twice: once with the initial token, and once
 * with a refreshed token if the first attempt returns 401.
 */
async function openStream(
  url: string,
  body: unknown,
  token: string,
  signal: AbortSignal,
): Promise<Response> {
  return fetch(`${API_BASE_URL}${url}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // fetch() supports SSE via ReadableStream — EventSource is not used here
      // because EventSource only supports GET requests without a body.
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  });
}

/**
 * Opens a POST SSE stream and dispatches parsed events to the provided callbacks.
 * Returns an abort function — call it on component unmount to cancel the stream
 * and prevent state updates on an unmounted component.
 *
 * LIFECYCLE:
 *   1. Get (or refresh) a valid Bearer token
 *   2. POST to the stream endpoint with Accept: text/event-stream
 *   3. On 401: refresh token once and retry — handles the rare case where the
 *      token expires in the gap between getValidToken() and the fetch
 *   4. Read chunks from the ReadableStream, accumulate into a buffer, parse SSE
 *      records, dispatch to onEvent/onDone
 *   5. If the reader loop exits without receiving a "done" event, call
 *      onError("stream-closed") — signals a dropped connection or server error
 *
 * WHY AN ASYNC IIFE INSIDE A SYNC FUNCTION:
 * useEffect cleanup functions must be synchronous. By returning the abort function
 * immediately and running the async work inside a void IIFE, callers get the
 * cleanup handle before any awaits, so React can call abort() during the cleanup
 * phase even if the stream hasn't opened yet.
 *
 */
export function openSSEStream<TData>(options: SSEClientOptions<TData>): () => void {
  const { url, body, onEvent, onError, onDone } = options;
  const controller = new AbortController();

  void (async () => {
    // ── 1. Ensure a valid token before opening the stream ─────────────────────
    let token: string | null;
    try {
      token = await getValidToken();
    } catch {
      onError("auth");
      return;
    }

    // Guard: component may have unmounted while we were awaiting the token
    if (controller.signal.aborted) return;

    if (!token) {
      onError("auth");
      return;
    }

    // ── 2. Open the stream ────────────────────────────────────────────────────
    let response: Response;
    try {
      response = await openStream(url, body, token, controller.signal);
    } catch {
      if (controller.signal.aborted) return;
      onError("network");
      return;
    }

    // ── 3. Retry on 401 with a refreshed token ────────────────────────────────
    // This path only runs if the token was valid in getValidToken() but expired
    // before the fetch completed (e.g. clock skew, or a very slow refresh delay).
    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (!newToken || controller.signal.aborted) {
        onError("auth");
        return;
      }
      try {
        response = await openStream(url, body, newToken, controller.signal);
      } catch {
        if (controller.signal.aborted) return;
        onError("network");
        return;
      }
    }

    if (!response.ok) {
      onError("network");
      return;
    }

    // response.body is null in environments that don't support streaming (e.g. some
    // test environments or older proxies). Guard here rather than crash on getReader().
    if (!response.body) {
      onError("network");
      return;
    }

    // ── 4. Read and parse the stream ──────────────────────────────────────────
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    // Mutable object so parseSseBuffer can flip the flag without returning it
    const doneFired = { value: false };

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = parseSseBuffer(buffer, onEvent, doneFired, onDone);
      }
    } catch {
      // AbortController.abort() causes reader.read() to reject — that's expected
      if (controller.signal.aborted) return;
      onError("network");
      return;
    }

    // ── 5. Detect premature stream close ─────────────────────────────────────
    // The reader loop exiting without a "done" event means the server closed the
    // connection before all widgets were delivered (dropped connection, timeout, etc.)
    if (!doneFired.value) {
      onError("stream-closed");
    }
  })();

  return () => controller.abort();
}
